import { DateTime } from '@ksyos/date-time';
import { Alert } from '@ksyos/design-system';
import classnames from 'classnames';
import Async from 'react-select/async';
import { getDisplayName } from '@common/utils/getDisplayName';
import { parseNameForSearch } from '@common/utils/parseNameForSearch';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import type { IReactSelectOptions } from '../interfaces/reactSelectOptions';
import { Feedback } from './common';
import { Component } from './Component';
import { FormLabel } from './forms/FormLabel';
import { HorizontalFormGroup } from './forms/HorizontalFormGroup';
import { InputContainer } from './forms/InputContainer';
import { SelectOption } from './SelectOption';

interface IServiceOrganizationConsumerSelectionProps {
    error?: string;
    excludePractitionerId?: string;
    hasErrors: boolean;
    initialOrganizationId: string | null;
    initialPractitionerId: string | null;
    isSupervisorSelector?: boolean;
    label: string;
    onChange: (organizationId: string | null, practitionerId: string | null) => Promise<void>;
    onDoValidate: () => void;
    requesterOrganizationId?: string;
    serviceId: string;
    serviceProviderId?: string;
}

interface IServiceOrganizationConsumerSelectionState {
    organizationPractitioners: IOrganizationPractitionerOptions[];
    selectedOptionLabel: string | null;
    unfilteredQueryHadResults: boolean | null;
}

interface IOrganizationPractitionerOptions {
    disabled: boolean;
    label: string;
    practitioners: {
        disabled: boolean;
        label: string;
        value: string;
    }[];
    value: string;
}

interface IPractitioner {
    absenceEndDate: string | null;
    id: string;
    isActivated: boolean;
    nameFamily?: string;
    nameGiven?: string;
    nameInfix?: string;
    zorgmailNummer?: string;
}

interface IServiceConsumerOrganizationsPractitioners {
    city: string;
    id: string;
    name: string;
    practitioners: IPractitioner[];
}

export class ServiceOrganizationConsumerSelection extends Component<
    IServiceOrganizationConsumerSelectionProps,
    IServiceOrganizationConsumerSelectionState
> {
    constructor(props: IServiceOrganizationConsumerSelectionProps, context?: {}) {
        super(props, context);

        this.state = {
            organizationPractitioners: [],
            selectedOptionLabel: null,
            unfilteredQueryHadResults: null,
        };
    }

    public async componentDidMount() {
        await this.loadInitialOrganizationPractitioner();
        await this.loadOrganizations();
    }

    public render() {
        const translate = getSelectedLanguage();

        return (
            <HorizontalFormGroup hasErrors={this.props.hasErrors}>
                <FormLabel isRequired>{this.props.label}</FormLabel>
                <InputContainer>
                    <>
                        {this.state.organizationPractitioners.length === 0 &&
                        this.state.unfilteredQueryHadResults === false ? (
                            <Alert appearance="warning">{this.getAlertMessage()}</Alert>
                        ) : (
                            <>
                                <Async<IReactSelectOptions>
                                    className={classnames(
                                        'select-form-control',
                                        'automation-sendTo',
                                        this.state.selectedOptionLabel ? 'selected-option' : ''
                                    )}
                                    classNamePrefix="Select"
                                    components={{ Option: SelectOption }}
                                    defaultOptions
                                    loadOptions={(input: string) => this.getOptions(input)}
                                    noOptionsMessage={() => translate.common.typeToSearch}
                                    onChange={async (selectedOption) => {
                                        if (selectedOption && !Array.isArray(selectedOption)) {
                                            await this.handleChange(selectedOption.value, selectedOption.label);
                                        }
                                    }}
                                    placeholder={this.state.selectedOptionLabel || translate.request.typeAndChoose}
                                />
                                <Feedback error={this.props.error} />
                            </>
                        )}
                    </>
                </InputContainer>
            </HorizontalFormGroup>
        );
    }

    private async handleChange(value?: string, label?: string) {
        await this.onChange(value || null);
        await this.setState({ selectedOptionLabel: label || null });
        if (this.props.onDoValidate) {
            await this.props.onDoValidate();
        }
    }

    private onChange(organizationPractitionerIds: string | null) {
        if (organizationPractitionerIds) {
            if (organizationPractitionerIds.includes('_')) {
                this.props.onChange(
                    organizationPractitionerIds.split('_')[0],
                    organizationPractitionerIds.split('_')[1]
                );
            } else {
                this.props.onChange(organizationPractitionerIds, null);
            }
        } else {
            this.props.onChange(null, null);
        }
    }

    private async loadOrganizations(searchInput?: string) {
        const result = await fetchJson(this.getUrl(searchInput), 'GET');

        const organizationPractitioners: IOrganizationPractitionerOptions[] = [];
        for (const organization of result.json.items as IServiceConsumerOrganizationsPractitioners[]) {
            const practitionerOptions: { disabled: boolean; label: string; value: string }[] = [];
            for (const practitioner of organization.practitioners) {
                const isEnabled =
                    practitioner.absenceEndDate === null && (practitioner.isActivated || practitioner.zorgmailNummer);
                practitionerOptions.push({
                    disabled: !isEnabled,
                    label: this.createPractitionerLabel(practitioner),
                    value: `${organization.id}_${practitioner.id}`,
                });
            }

            organizationPractitioners.push({
                disabled: true,
                label: this.createOrganizationLabel(organization),
                practitioners: practitionerOptions,
                value: organization.id,
            });
        }

        if (this.state.unfilteredQueryHadResults === null) {
            await this.setState({ unfilteredQueryHadResults: organizationPractitioners.length > 0 });
        }
        await this.setState({ organizationPractitioners });
    }

    private async getOptions(input: string) {
        await this.loadOrganizations(input);
        const options: { className?: string; isDisabled: boolean; label: string; value: string }[] = [];
        this.state.organizationPractitioners.forEach((organization) => {
            const className = organization.disabled ? 'Select__group-header is-disabled' : 'Select__group-header';
            options.push({
                className: className,
                isDisabled: organization.disabled,
                label: organization.label,
                value: organization.value,
            });
            for (const practitioner of organization.practitioners) {
                const nameClass = practitioner.disabled ? 'Select__group-option is-disabled' : 'Select__group-option';
                options.push({
                    className: nameClass,
                    isDisabled: practitioner.disabled,
                    label: practitioner.label,
                    value: practitioner.value,
                });
            }
        });

        return options;
    }

    private async loadInitialOrganizationPractitioner() {
        if (this.props.initialOrganizationId) {
            let url = `/api/organizations/${this.props.initialOrganizationId}`;
            const organizationResult = await fetchJson(url, 'GET');
            let selectedOptionLabel = organizationResult.json
                ? this.createOrganizationLabel(organizationResult.json)
                : null;
            if (this.props.initialPractitionerId) {
                url = `/api/practitioners/${this.props.initialPractitionerId}`;
                const practitionerResult = await fetchJson(url, 'GET');
                selectedOptionLabel = practitionerResult.json
                    ? this.createPractitionerLabel(practitionerResult.json)
                    : null;
            }
            await this.setState({ selectedOptionLabel });
        }
    }

    private createPractitionerLabel(practitioner: IPractitioner) {
        const translate = getSelectedLanguage();
        let label = getDisplayName(practitioner);
        if (practitioner.absenceEndDate) {
            label += ` (${translate.request.absentUpToAndIncluding} ${DateTime.from(practitioner.absenceEndDate).format(
                'DD-MM-YYYY'
            )})`;
        }

        return label;
    }

    private createOrganizationLabel(organization: IServiceConsumerOrganizationsPractitioners) {
        return `${organization.name} ${organization.city ? `(${organization.city})` : ''}`;
    }

    private getUrl(searchInput?: string) {
        const searchText = searchInput ? encodeURIComponent(parseNameForSearch(searchInput)) : '';
        let urlBuilder;
        if (this.props.serviceProviderId && !this.props.isSupervisorSelector) {
            urlBuilder = new UrlBuilder(
                `/api/services/${this.props.serviceId}/serviceConsumers/preferredOrganizationsAndPractitioners/${this.props.serviceProviderId}`
            );
            urlBuilder.add('searchText', searchText);
            urlBuilder.add('serviceId', this.props.serviceId);
        } else if (this.props.isSupervisorSelector) {
            urlBuilder = new UrlBuilder(
                `/api/services/${this.props.serviceId}/serviceConsumers/organizationsAndPractitioners`
            );
            urlBuilder.add('searchPractitioner', searchText);

            if (this.props.requesterOrganizationId) {
                urlBuilder.add('organizationId', this.props.requesterOrganizationId);
            }

            if (this.props.excludePractitionerId) {
                urlBuilder.add('excludePractitionerId', this.props.excludePractitionerId);
            }
        } else {
            throw new Error('Can not build url');
        }

        return urlBuilder.build();
    }

    private getAlertMessage() {
        const translate = getSelectedLanguage();
        let message = null;

        if (this.props.isSupervisorSelector) {
            message = translate.request.noSupervisorForRequest;
        } else {
            message = translate.request.noConsumerForRequest;
        }

        return message;
    }
}
